import React from "react";
import { Box, Stack, Typography, Chip, Button } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import VideocamIcon from "@mui/icons-material/Videocam";
import { Link as RouterLink } from "react-router-dom";
import { groupRoleConfig, staffRoleLabel } from "../tutorStatusChips";

/**
 * The tutor's groups, with role, headcount and the delivery window.
 *
 * `student_count` comes from CoursePlanSeatMapper::enrolledCount, so it counts legacy enrolments
 * that carry only a course_price_id as well as modern ones. The group's seat limit (max_seats) is
 * not exposed by `tutor/groups`, so this shows headcount rather than an occupancy bar.
 *
 * A group split by subject shows the subjects this tutor holds — that is what decides which classes
 * they may host — and, for a lead, who assists them. A group with no split shows neither, because
 * there is nothing to say beyond the role chip.
 */
const MAX_ROWS = 6;

/** The lead's name, with their subjects when the group is split. */
const leadLabel = (g) => {
  const lead = (g.staff || []).find((m) => m.role === "tutor");
  if (!lead) return g.lead_tutor;
  return lead.subjects?.length
    ? `${lead.name} (${lead.subjects.map((s) => s.name).join(", ")})`
    : lead.name || g.lead_tutor;
};

/** The group's assistants, excluding the caller — they know who they are. */
const assistantsOf = (g) =>
  (g.staff || []).filter((m) => m.role === "helper" && !m.is_me);

const dateRange = (g) => {
  const fmt = (d) => (d ? new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null);
  const from = fmt(g.start_date);
  const to = fmt(g.end_date);
  if (from && to) return `${from} – ${to}`;
  return from || to || null;
};

export default function TutorGroupsList({ groups }) {
  const rows = (groups || []).slice(0, MAX_ROWS);

  if (rows.length === 0) {
    return (
      <Stack alignItems="center" spacing={1} sx={{ py: 4, textAlign: "center" }}>
        <GroupsIcon sx={{ fontSize: 40, color: "rgba(0,0,0,0.26)" }} />
        <Typography variant="subtitle1" fontWeight={600}>
          No groups yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          An admin assigns you to a group before you can upload content or host its classes.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.25}>
      {rows.map((g) => {
        const base = groupRoleConfig[g.my_role] || { label: g.my_role, color: "#616161" };
        // Name the role by what they actually hold: "Maths tutor" beats "Tutor Assistant" when
        // they own Maths here.
        const role = {
          ...base,
          label: staffRoleLabel(g.my_role, g.my_subjects || [], { short: true }),
        };
        const meta = [
          g.is_active ? `${g.student_count ?? 0} students` : "Ended",
          g.location || (g.mode === "online" ? "Online" : null),
          dateRange(g),
        ].filter(Boolean);
        return (
          <Stack
            key={g.id}
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ opacity: g.is_active ? 1 : 0.65 }}
          >
            <Box
              sx={{ width: 4, alignSelf: "stretch", minHeight: 38, borderRadius: 1, bgcolor: role.color, flexShrink: 0 }}
            />
            <Box
              component={RouterLink}
              to="/tutor/groups"
              sx={{ minWidth: 0, flex: 1, textDecoration: "none" }}
            >
              <Typography variant="body2" fontWeight={700} noWrap sx={{ color: "#111827" }}>
                {g.name}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b7280" }} noWrap display="block">
                {g.course?.name ? `${g.course.name} · ` : ""}{meta.join(" · ")}
              </Typography>
              {g.my_role !== "tutor" && g.lead_tutor && (
                <Typography variant="caption" sx={{ color: "#9aa1ac" }} noWrap display="block">
                  {/* Their subjects matter to an assistant: they say which classes are the lead's
                      to host and which are the assistant's own. */}
                  Lead: {leadLabel(g)}
                </Typography>
              )}
              {(g.my_subjects || []).length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                  {g.my_subjects.map((s) => (
                    <Chip
                      key={s.id}
                      size="small"
                      label={s.name}
                      sx={{
                        height: 20,
                        bgcolor: "#eef2ff",
                        color: "#4338ca",
                        fontWeight: 600,
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  ))}
                </Stack>
              )}
              {assistantsOf(g).length > 0 && (
                <Typography variant="caption" sx={{ color: "#9aa1ac" }} noWrap display="block">
                  {g.my_role === "tutor" ? "Assisted by" : "Also assisting"}:{" "}
                  {assistantsOf(g)
                    .map((m) => (m.subjects.length ? `${m.name} (${m.subjects.map((s) => s.name).join(", ")})` : m.name))
                    .join(" · ")}
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
              <Chip
                size="small"
                label={role.label}
                sx={{ bgcolor: `${role.color}18`, color: role.color, fontWeight: 700 }}
              />
              {g.can_host_class && g.is_active && (
                <Button
                  size="small"
                  component={RouterLink}
                  to="/tutor/classes/schedule"
                  startIcon={<VideocamIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none", fontWeight: 600, minWidth: 0 }}
                >
                  Host
                </Button>
              )}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
}
